import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Send, Upload, X } from "lucide-react";
import { api, errMsg, saveBlob } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const input = "h-9 px-2 rounded-lg bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm";
const slug = (d) => d.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]+/g, "-");
const title = (s) => s.team_name || s.members.join(" i ");
const MEDIA = { music: "muzyka", music_photo: "muzyka + zdjęcie", video: "animacja" };
const Media = ({ m }) => <span className="text-amber-300">{MEDIA[m] || "brak multimediów"}</span>;

export const PlanTab = ({ onExported }) => {
  const [plan, setPlan] = useState(null);
  const [newDay, setNewDay] = useState("");
  const [cat, setCat] = useState("");
  const [bulkDay, setBulkDay] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const load = () => api.get("/admin/plan").then((r) => setPlan(r.data));
  useEffect(() => { load(); }, []);
  const call = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  const unassigned = useMemo(() => (plan?.signups || []).filter((s) => !s.plan_day && (!cat || s.category === cat)), [plan, cat]);
  const cats = useMemo(() => [...new Set((plan?.signups || []).map((s) => s.category))].sort(), [plan]);
  if (!plan) return <p className="text-slate-400 mt-6">Ładowanie…</p>;
  const setDays = (days) => call(() => api.put("/admin/plan/days", { days }), "Zapisano dni zawodów");
  const byDay = (d) => [...plan.signups.filter((s) => s.plan_day === d).map((s) => ({ ...s, kind: "signup" })),
    ...(plan.breaks || []).filter((b) => b.plan_day === d).map((b) => ({ ...b, kind: "break" }))].sort((a, b) => (a.plan_order || 0) - (b.plan_order || 0));

  return (
    <div className="mt-6 grid gap-6" data-testid="plan-tab">
      <section className="fly-card p-5">
        <h2 className="font-display text-xl font-bold uppercase">Dni zawodów</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {plan.days.map((d) => (
            <span key={d} className="h-9 pl-3 pr-1 rounded-full bg-blue-600/20 border border-blue-500/40 text-sm flex items-center gap-1" data-testid={`plan-day-chip-${slug(d)}`}>{d}
              <button data-testid={`plan-day-remove-${slug(d)}`} onClick={() => window.confirm(`Usunąć dzień ${d}? Przypisani zawodnicy wrócą do nieprzypisanych.`) && setDays(plan.days.filter((x) => x !== d))} className="h-7 w-7 rounded-full hover:bg-red-500/20 text-red-300 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
          <form onSubmit={(e) => { e.preventDefault(); if (newDay.trim()) { setDays([...plan.days, newDay]); setNewDay(""); } }} className="flex gap-2">
            <input data-testid="plan-day-input" value={newDay} onChange={(e) => setNewDay(e.target.value)} placeholder="np. CZWARTEK" className={input} />
            <button data-testid="plan-day-add" className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm flex items-center gap-1"><Plus className="w-4 h-4" />Dodaj dzień</button>
          </form>
        </div>
      </section>

      <section className="fly-card p-5" data-testid="plan-unassigned">
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <h2 className="font-display text-xl font-bold uppercase">Nieprzypisani ({unassigned.length})</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="plan-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)} className={input}>
              <option value="">Wszystkie kategorie</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="plan-bulk-day" value={bulkDay} onChange={(e) => setBulkDay(e.target.value)} className={input}>
              <option value="">— dzień —</option>
              {plan.days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <button data-testid="plan-bulk-btn" disabled={!cat || !bulkDay} onClick={() => call(() => api.post("/admin/plan/bulk", { category: cat, day: bulkDay }), `Przypisano kategorię do dnia ${bulkDay}`)}
              className="h-9 px-3 rounded-lg border border-blue-500/40 text-blue-200 text-sm disabled:opacity-40">Przypisz całą kategorię</button>
          </div>
        </div>
        <div className="grid gap-2 mt-3">
          {unassigned.map((s) => <Row key={s.id} s={s} days={plan.days} onAssign={(day) => call(() => api.put(`/admin/plan/signups/${s.id}`, { day, time: "" }))} />)}
          {!unassigned.length && <p className="text-sm text-slate-500">Wszystkie zgłoszenia są przypisane.</p>}
        </div>
      </section>

      {plan.days.map((d) => <DaySection key={d} day={d} items={byDay(d)} call={call} />)}

      <section className="fly-card p-5 border-red-500/40 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-300 max-w-2xl">EKSPORT zastępuje całą listę startową w Organizacji tym planem (z godzinami, kolejnością i przerwami). Eksportowane są tylko <b>zatwierdzone</b> zgłoszenia. Usuwa obecne oceny, rejestrację i stan konferansjera; najpierw zapisuje i pobiera kopię wyników.
          <span data-testid="plan-counts" className="block mt-1 text-xs font-mono"><span className="text-emerald-400">zatwierdzone: {plan.counts?.approved ?? 0}</span> · <span className="text-amber-300">oczekujące: {plan.counts?.pending ?? 0}</span> · <span className="text-red-300">odrzucone: {plan.counts?.rejected ?? 0}</span></span></p>
        <button data-testid="plan-export-btn" onClick={() => setExportOpen(true)} className="h-12 px-6 rounded-xl bg-red-600 hover:bg-red-500 font-display text-lg font-bold uppercase flex items-center gap-2"><Upload className="w-5 h-5" />Eksport</button>
      </section>
      <ExportDialog open={exportOpen} counts={plan.counts || {}} onClose={() => setExportOpen(false)} onDone={() => { setExportOpen(false); load(); onExported?.(); }} />
    </div>
  );
};

const Row = ({ s, days, onAssign }) => (
  <div className="rounded-lg bg-[#0B132B]/60 border border-white/5 px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`plan-row-${s.number}`}>
    <span className="font-mono text-xs text-blue-300">{s.number}</span>
    <span className="font-semibold flex-1 min-w-[180px]">{title(s)}<span className="block text-xs text-slate-400 font-normal">{s.category} · {s.age_group} · {s.school || "niezrzeszony"} · <Media m={s.media} /> · <span className={s.status === "approved" ? "text-emerald-400" : "text-amber-300"}>{s.status === "approved" ? "zatwierdzone" : "oczekuje"}</span></span></span>
    <select data-testid={`plan-assign-${s.number}`} value="" onChange={(e) => e.target.value && onAssign(e.target.value)} className={input}>
      <option value="">przypisz do dnia…</option>
      {days.map((d) => <option key={d} value={d}>{d}</option>)}
    </select>
  </div>
);

const DaySection = ({ day, items, call }) => {
  const n = items.filter((x) => x.kind === "signup").length;
  return (
  <section className="fly-card p-5" data-testid={`plan-day-${slug(day)}`}>
    <h2 className="font-display text-xl font-bold uppercase">{day} <span className="text-slate-500 text-sm">({n})</span></h2>
    <Schedule day={day} items={items} call={call} />
    <div className="grid gap-2 mt-3">
      {items.map((s, i) => (
        <div key={s.id} className={`rounded-lg border px-3 py-2 flex flex-wrap items-center gap-2 ${s.kind === "break" ? "bg-amber-500/10 border-amber-500/30" : "bg-[#0B132B]/60 border-white/5"}`} data-testid={s.kind === "break" ? `plan-break-${s.id}` : `plan-item-${s.number}`}>
          <span className="font-mono text-xs text-slate-400 w-6">{i + 1}.</span>
          {s.kind === "break" ? <span className="font-mono text-sm w-28">{s.plan_time || "—"}</span> : <TimeInput key={s.plan_time} s={s} onSave={(time) => call(() => api.put(`/admin/plan/signups/${s.id}`, { day, time }))} />}
          {s.kind === "break"
            ? <span className="font-semibold flex-1 min-w-[160px] text-amber-200">PRZERWA – {s.label} ({s.minutes} min)</span>
            : <span className="font-semibold flex-1 min-w-[160px]">{title(s)}<span className="block text-xs text-slate-400 font-normal">{s.category} · {s.age_group} · {s.school || "niezrzeszony"} · <Media m={s.media} />{s.status !== "approved" && <span className="text-amber-300"> · oczekuje (nie trafi do eksportu)</span>}</span></span>}
          <button data-testid={`plan-up-${s.number || s.id}`} disabled={i === 0} onClick={() => call(() => api.post("/admin/plan/move", { sid: s.id, dir: -1 }))} className="h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
          <button data-testid={`plan-down-${s.number || s.id}`} disabled={i === items.length - 1} onClick={() => call(() => api.post("/admin/plan/move", { sid: s.id, dir: 1 }))} className="h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
          <button data-testid={`plan-unassign-${s.number || s.id}`} title="Usuń z dnia" onClick={() => call(() => (s.kind === "break" ? api.delete(`/admin/plan/breaks/${s.id}`) : api.put(`/admin/plan/signups/${s.id}`, { day: null, time: "" })))} className="h-9 w-9 rounded-lg text-red-400 hover:bg-red-500/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {!items.length && <p className="text-sm text-slate-500">Brak zawodników w tym dniu.</p>}
    </div>
    <AddBreak day={day} call={call} />
  </section>
  );
};

const Schedule = ({ day, items, call }) => {
  const [start, setStart] = useState("10:00");
  const [interval, setInterval] = useState(5);
  const [sort, setSort] = useState(true);
  const [per, setPer] = useState({});
  const cats = [...new Set(items.filter((x) => x.kind === "signup").map((x) => x.category))];
  if (!cats.length) return null;
  const run = () => call(async () => {
    const { data } = await api.post("/admin/plan/schedule", { day, start, interval: Number(interval) || 5, intervals: per, sort_by_category: sort });
    toast.success(`Ułożono godziny: ${start}–${data.end}`);
  });
  return (
    <div className="mt-3 rounded-xl bg-blue-600/10 border border-blue-500/20 p-3 grid gap-2" data-testid={`plan-schedule-${slug(day)}`}>
      <div className="flex flex-wrap items-end gap-3 text-xs text-slate-400">
        <label className="grid gap-1">Start zawodów<input type="time" data-testid={`plan-start-${slug(day)}`} value={start} onChange={(e) => setStart(e.target.value)} className={`${input} w-28 [color-scheme:dark]`} /></label>
        <label className="grid gap-1">Co ile minut<input type="number" min={1} max={120} data-testid={`plan-interval-${slug(day)}`} value={interval} onChange={(e) => setInterval(e.target.value)} className={`${input} w-20`} /></label>
        <label className="flex items-center gap-2 h-9 text-sm text-slate-200"><input type="checkbox" data-testid={`plan-sort-${slug(day)}`} checked={sort} onChange={(e) => setSort(e.target.checked)} />segreguj według kategorii</label>
        <span className="text-[11px] text-slate-500">godziny dostają tylko zatwierdzone zgłoszenia</span>
        <button data-testid={`plan-schedule-btn-${slug(day)}`} onClick={run} className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">Ułóż godziny</button>
      </div>
      <details className="text-xs text-slate-400">
        <summary className="cursor-pointer text-blue-300">Inny czas dla wybranej kategorii</summary>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {cats.map((c) => (
            <label key={c} className="flex items-center gap-2"><span className="flex-1 truncate">{c}</span>
              <input type="number" min={1} max={120} placeholder={String(interval)} value={per[c] || ""} onChange={(e) => setPer({ ...per, [c]: e.target.value ? Number(e.target.value) : undefined })} className={`${input} w-20`} />min</label>
          ))}
        </div>
      </details>
    </div>
  );
};

const AddBreak = ({ day, call }) => {
  const [label, setLabel] = useState("Rozdanie nagród");
  const [minutes, setMinutes] = useState(15);
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`plan-add-break-${slug(day)}`}>
      <input list="break-labels" data-testid={`plan-break-label-${slug(day)}`} value={label} onChange={(e) => setLabel(e.target.value)} className={`${input} w-56`} />
      <datalist id="break-labels"><option value="Rozdanie nagród" /><option value="Przerwa techniczna" /></datalist>
      <input type="number" min={1} max={600} data-testid={`plan-break-minutes-${slug(day)}`} value={minutes} onChange={(e) => setMinutes(e.target.value)} className={`${input} w-20`} /><span className="text-xs text-slate-400">min</span>
      <button data-testid={`plan-break-add-${slug(day)}`} onClick={() => label.trim() && call(() => api.post("/admin/plan/breaks", { day, label: label.trim(), minutes: Number(minutes) || 15 }), "Dodano przerwę na końcu dnia")} className="h-9 px-3 rounded-lg border border-amber-500/40 text-amber-200 text-sm flex items-center gap-1"><Plus className="w-4 h-4" />Dodaj przerwę</button>
    </div>
  );
};

const TimeInput = ({ s, onSave }) => {
  const [v, setV] = useState(s.plan_time || "");
  return <input type="time" data-testid={`plan-time-${s.number}`} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (s.plan_time || "") && onSave(v)} className={`${input} w-28 [color-scheme:dark]`} />;
};

export const SendToOrganizer = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const show = () => { api.get("/admin/plan").then((r) => setCounts(r.data.counts || {})); setOpen(true); };
  return (
    <>
      <button data-testid="send-to-organizer-btn" onClick={show} className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold flex items-center gap-2"><Send className="w-4 h-4" />Prześlij do Organizatora</button>
      <ExportDialog open={open} counts={counts} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />
    </>
  );
};

const ExportDialog = ({ open, counts, onClose, onDone }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/plan/export", { password: pw });
      try {
        const r = await api.get(`/admin/backups/${data.backup_id}/file.xlsx`, { responseType: "blob" });
        saveBlob(r.data, data.filename);
      } catch { toast.error("Nie udało się pobrać kopii – jest dostępna w kopiach zapasowych"); }
      const n = data.exported, w = n === 1 ? "występ" : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? "występy" : "występów";
      toast.success(`Wyeksportowano ${n} ${w} do listy startowej`);
      setPw("");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1C2541] border-red-500/40" data-testid="plan-export-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase text-red-300">Eksport do listy startowej</DialogTitle></DialogHeader>
        <form onSubmit={run} className="grid gap-3">
          <p className="text-sm text-slate-300">Obecna lista startowa, oceny i rejestracja zostaną zastąpione. Przechodzą tylko zatwierdzone zgłoszenia przypisane do dnia w Planie zawodów (z godziną i kolejnością): <b className="text-emerald-400">{counts.approved ?? 0}</b>. Pominięte: oczekujące <b className="text-amber-300">{counts.pending ?? 0}</b>, odrzucone <b className="text-red-300">{counts.rejected ?? 0}</b>. Podaj hasło administratora.</p>
          <input data-testid="plan-export-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 px-3 rounded-xl bg-[#0B132B] border border-red-500/30 text-sm" />
          <button data-testid="plan-export-confirm" disabled={busy || !pw} className="h-11 rounded-xl bg-red-600 hover:bg-red-500 font-semibold disabled:opacity-50">{busy ? "Eksportuję…" : "Eksportuj"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
